<template>
  <div class="channel-config">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <div class="title-icon">
            <el-icon>
              <Setting />
            </el-icon>
          </div>
          <div>
            <h2 class="page-title">渠道配置</h2>
            <p class="page-desc">配置小程序渠道参数与域名信息</p>
          </div>
        </div>
      </div>
    </div>

    <div class="config-container">
      <!-- 小程序配置卡片 -->
      <el-card class="config-card" shadow="never">
        <template #header>
          <div class="card-header">
            <div class="card-header-title">
              <div class="icon-wrapper primary">
                <el-icon>
                  <Phone />
                </el-icon>
              </div>
              <span>小程序配置</span>
            </div>
            <el-tag type="primary" size="small">基础信息</el-tag>
          </div>
        </template>

        <el-form :model="form" ref="formRef" label-width="120px" class="config-form">
          <el-form-item label="AppID" required>
            <el-input v-model="form.appid" placeholder="请输入小程序AppID" size="large" />
          </el-form-item>

          <el-form-item label="AppSecret" required>
            <el-input v-model="form.appsecret" type="password" placeholder="请输入小程序AppSecret" show-password
              size="large" />
            <div class="form-tip">修改时需要重新输入，留空表示保持不变</div>
          </el-form-item>

          <el-form-item label="上传密钥">
            <el-input v-model="form.upload_key" type="textarea" :rows="4" placeholder="请输入小程序上传密钥（RSA私钥）" />
            <div class="form-tip">用于上传小程序代码，格式为 PEM 格式的私钥</div>
          </el-form-item>

          <el-form-item class="save-btn-item">
            <div class="btn-group">
              <el-button type="primary" @click="saveConfig" :loading="submitting" size="large">
                <el-icon>
                  <Check />
                </el-icon>保存配置
              </el-button>
              <el-button type="success" @click="openUploadCodeDialog" size="large">
                <el-icon>
                  <Upload />
                </el-icon>上传小程序代码
              </el-button>
            </div>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- 服务器域名配置卡片 -->
      <el-card class="config-card domain-card" shadow="never">
        <template #header>
          <div class="card-header">
            <div class="card-header-title">
              <div class="icon-wrapper info">
                <el-icon>
                  <Link />
                </el-icon>
              </div>
              <span>服务器域名配置</span>
            </div>
            <el-tag type="info" size="small">微信小程序后台配置</el-tag>
          </div>
        </template>

        <div class="domain-list">
          <div v-for="(domain, index) in serverDomains" :key="index" class="domain-item">
            <div class="domain-info">
              <div class="domain-label">
                <span class="label-text">{{ domain.label }}</span>
                <el-tag :type="domain.tagType" size="small" effect="plain">{{ domain.protocol }}</el-tag>
                <el-tag v-if="domain.isOss" type="warning" size="small" effect="plain">OSS域名</el-tag>
              </div>
              <div class="domain-value-wrapper">
                <div class="domain-value">{{ domain.value }}</div>
                <el-button class="copy-btn" type="primary" circle size="small" @click="copyDomain(domain.value)">
                  <el-icon>
                    <CopyDocument />
                  </el-icon>
                </el-button>
              </div>
              <div class="domain-tip" v-if="domain.tip">
                <el-icon>
                  <InfoFilled />
                </el-icon>
                <span>{{ domain.tip }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- OSS配置状态提示 -->
        <el-alert v-if="ossDomainStatus" :title="ossDomainStatus.title" :type="ossDomainStatus.type" :closable="false"
          class="domain-alert">
          <template #default>
            <div class="alert-content">
              <p>{{ ossDomainStatus.message }}</p>
              <p v-if="ossDomainStatus.source">配置来源：{{ ossDomainStatus.source }}</p>
            </div>
          </template>
        </el-alert>

        <el-alert title="配置说明" type="info" :closable="false" class="domain-alert">
          <template #default>
            <div class="alert-content">
              <p>请将以上域名配置到微信小程序后台的以下位置：</p>
              <p><strong>开发 → 开发管理 → 开发设置 → 服务器域名</strong></p>
              <p class="alert-tip">💡 提示：域名间请用英文分号「;」分隔，不支持端口号和 IP 地址</p>
            </div>
          </template>
        </el-alert>
      </el-card>

      <!-- 业务域名配置卡片 -->
      <el-card class="config-card business-card" shadow="never">
        <template #header>
          <div class="card-header">
            <div class="card-header-title">
              <div class="icon-wrapper success">
                <el-icon>
                  <FolderOpened />
                </el-icon>
              </div>
              <span>业务域名配置</span>
            </div>
            <el-tag type="success" size="small">文件验证</el-tag>
          </div>
        </template>

        <div class="business-content">
          <!-- 业务域名展示 -->
          <div class="business-domain-section">
            <div class="section-label">
              <el-icon>
                <Location />
              </el-icon>
              <span>业务域名</span>
            </div>
            <div class="business-domain-wrapper">
              <div class="domain-value large">{{ businessDomain }}</div>
              <el-button type="primary" @click="copyDomain(businessDomain)">
                <el-icon>
                  <CopyDocument />
                </el-icon>复制域名
              </el-button>
            </div>
            <div class="domain-tip business-tip">
              <el-icon>
                <InfoFilled />
              </el-icon>
              <span>以 https:// 开头，可填写 200 个域名，域名间请用 ; 分割</span>
            </div>
          </div>

          <el-divider />

          <!-- 验证文件上传 -->
          <div class="upload-section">
            <div class="section-label">
              <el-icon>
                <UploadFilled />
              </el-icon>
              <span>上传验证文件</span>
            </div>

            <div class="upload-description">
              <el-alert title="配置说明" type="warning" :closable="false" show-icon>
                <template #default>
                  <div>
                    <p>1. 在微信小程序后台「开发设置」-「业务域名」中添加当前域名</p>
                    <p>2. 下载验证文件（.txt格式）</p>
                    <p>3. 将下载的文件上传到下方，系统会自动保存到服务器根目录</p>
                  </div>
                </template>
              </el-alert>
            </div>

            <!-- 已上传文件信息 -->
            <div v-if="verifyFileInfo" class="file-info-card">
              <div class="file-icon">
                <el-icon>
                  <Document />
                </el-icon>
              </div>
              <div class="file-details">
                <div class="file-name">{{ verifyFileInfo.file_name }}</div>
                <div class="file-meta">
                  <span>上传时间：{{ verifyFileInfo.upload_time }}</span>
                  <span>文件大小：{{ formatFileSize(verifyFileInfo.file_size) }}</span>
                </div>
              </div>
              <div class="file-actions">
                <el-button type="danger" link @click="deleteVerifyFile" :loading="deletingFile">
                  <el-icon>
                    <Delete />
                  </el-icon>删除文件
                </el-button>
              </div>
            </div>

            <!-- 上传组件 -->
            <el-upload class="domain-upload" drag :action="uploadActionUrl" :data="uploadFormData"
              :before-upload="beforeUpload" :on-success="handleUploadSuccess" :on-error="handleUploadError" :limit="1"
              name="domain_file" accept=".txt" :file-list="fileList">
              <el-icon class="upload-icon">
                <UploadFilled />
              </el-icon>
              <div class="upload-text">
                <span class="primary-text">点击或拖拽文件至此区域上传</span>
                <span class="secondary-text">支持 .txt 格式，文件大小不超过 100KB</span>
              </div>
            </el-upload>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 上传代码弹窗 -->
    <el-dialog v-model="uploadCodeDialogVisible" title="上传小程序代码" width="700px" destroy-on-close class="upload-dialog">
      <el-form :model="uploadForm" label-width="120px">
        <el-form-item label="小程序AppID" required>
          <el-input v-model="uploadForm.appid" disabled />
          <div class="form-tip">从小程序配置中自动获取</div>
        </el-form-item>
        <el-form-item label="上传私钥" required>
          <el-input v-model="uploadForm.privateKeyContent" type="textarea" :rows="6" disabled />
          <div class="form-tip">从小程序配置中自动获取（upload_key）</div>
        </el-form-item>
        <el-form-item label="API域名" required>
          <el-input v-model="uploadForm.api_url" disabled />
          <div class="form-tip">自动使用系统配置的API域名</div>
        </el-form-item>
        <el-form-item label="商家ID" required>
          <el-input-number v-model="uploadForm.default_store_id" :disabled="true" :min="1" />
          <div class="form-tip">自动使用当前商家ID</div>
        </el-form-item>
      </el-form>

      <div v-if="uploading" class="upload-progress">
        <el-progress :percentage="uploadProgress" :status="uploadProgressStatus" />
        <div class="upload-log">
          <div v-for="(log, index) in uploadLogs" :key="index" class="log-line">{{ log }}</div>
        </div>
      </div>

      <template #footer>
        <el-button @click="uploadCodeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitUploadCode" :loading="uploading" :disabled="uploading">
          {{ uploading ? '上传中...' : '开始上传' }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElLoading, ElMessageBox } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import {
  Phone, Check, Upload, Link, CopyDocument, FolderOpened,
  Document, Delete, Setting, Location, UploadFilled, InfoFilled
} from '@element-plus/icons-vue'

export default {
  name: 'ChannelConfig',
  components: {
    Phone, Check, Upload, Link, CopyDocument, FolderOpened,
    Document, Delete, Setting, Location, UploadFilled, InfoFilled
  },
  setup() {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const storeId = ref(userInfo.id || 0)
    const agentId = ref(userInfo.agent_id || 0)

    const formRef = ref(null)
    const submitting = ref(false)
    const uploading = ref(false)
    const uploadProgress = ref(0)
    const uploadProgressStatus = ref('')
    const uploadLogs = ref([])
    const uploadCodeDialogVisible = ref(false)
    const verifyFileInfo = ref(null)
    const deletingFile = ref(false)
    const fileList = ref([])

    // OSS域名相关
    const ossDomainInfo = ref(null)
    const ossDomainLoading = ref(false)

    const form = reactive({
      appid: '',
      appsecret: '',
      upload_key: ''
    })

    const uploadForm = reactive({
      appid: '',
      privateKeyContent: '',
      api_url: config.BASE_URL,
      default_store_id: storeId.value
    })

    // 上传接口地址
    const uploadActionUrl = computed(() => `${config.BASE_URL}/shop/api/upload_domain_file.php`)

    // 上传时需要的额外数据
    const uploadFormData = computed(() => ({
      agent_id: agentId.value,
      store_id: storeId.value
    }))

    // 获取当前域名
    const getCurrentDomain = () => {
      let host = window.location.host
      host = host.split(':')[0]
      return host
    }

    // OSS域名状态提示
    const ossDomainStatus = computed(() => {
      if (ossDomainLoading.value) {
        return {
          title: '加载中',
          type: 'info',
          message: '正在获取OSS域名配置...',
          source: null
        }
      }

      if (!ossDomainInfo.value) {
        return {
          title: 'OSS域名未配置',
          type: 'warning',
          message: '未能获取到OSS域名配置，uploadFile和downloadFile域名将只包含主域名',
          source: null
        }
      }

      if (!ossDomainInfo.value.config_complete) {
        return {
          title: 'OSS配置不完整',
          type: 'error',
          message: 'OSS配置不完整，请联系管理员检查配置',
          source: null
        }
      }

      return {
        title: 'OSS域名已配置',
        type: 'success',
        message: `已自动获取OSS外网域名：${ossDomainInfo.value.oss_domain}`,
        source: ossDomainInfo.value.source_text
      }
    })

    // 服务器域名配置列表（完整8个，包含OSS域名）
    const serverDomains = computed(() => {
      const domain = getCurrentDomain()
      const baseDomains = [
        {
          label: 'request合法域名',
          value: `https://${domain}`,
          protocol: 'HTTPS',
          tagType: 'primary',
          tip: '以 https:// 开头，可填写 300 个域名',
          isOss: false
        },
        {
          label: 'socket合法域名',
          value: `wss://${domain}`,
          protocol: 'WSS',
          tagType: 'warning',
          tip: '以 wss:// 开头，可填写 200 个域名',
          isOss: false
        },
        {
          label: 'uploadFile合法域名',
          value: getUploadFileDomains(),
          protocol: 'HTTPS',
          tagType: 'primary',
          tip: '以 https:// 开头，可填写 200 个域名，多个域名请用分号分隔',
          isOss: false
        },
        {
          label: 'downloadFile合法域名',
          value: getDownloadFileDomains(),
          protocol: 'HTTPS',
          tagType: 'primary',
          tip: '以 https:// 开头，可填写 200 个域名，多个域名请用分号分隔',
          isOss: false
        },
        {
          label: 'udp合法域名',
          value: `udp://${domain}`,
          protocol: 'UDP',
          tagType: 'info',
          tip: '以 udp:// 开头，可填写 200 个域名',
          isOss: false
        },
        {
          label: 'tcp合法域名',
          value: `tcp://${domain}`,
          protocol: 'TCP',
          tagType: 'info',
          tip: '以 tcp:// 开头，可填写 200 个域名',
          isOss: false
        },
        {
          label: 'DNS预解析域名',
          value: domain,
          protocol: 'DNS',
          tagType: 'success',
          tip: '无需填写协议头，最多填写 5 个域名',
          isOss: false
        },
        {
          label: '预连接域名',
          value: `https://${domain}`,
          protocol: 'HTTPS',
          tagType: 'danger',
          tip: '以 https:// 开头，需为 request 合法域名与 DNS 预解析域名的子集，可填写 5 个域名',
          isOss: false
        }
      ]

      // 如果OSS配置完整，为uploadFile和downloadFile添加OSS域名标识
      if (ossDomainInfo.value && ossDomainInfo.value.config_complete && ossDomainInfo.value.oss_domain) {
        baseDomains[2].isOss = true
        baseDomains[3].isOss = true
      }

      return baseDomains
    })

    // 获取uploadFile域名列表（主域名 + OSS域名）
    const getUploadFileDomains = () => {
      const domain = getCurrentDomain()
      const mainDomain = `https://${domain}`

      if (ossDomainInfo.value && ossDomainInfo.value.config_complete && ossDomainInfo.value.oss_domain) {
        // 使用分号分隔多个域名
        return `${mainDomain};${ossDomainInfo.value.oss_domain}`
      }

      return mainDomain
    }

    // 获取downloadFile域名列表（主域名 + OSS域名）
    const getDownloadFileDomains = () => {
      const domain = getCurrentDomain()
      const mainDomain = `https://${domain}`

      if (ossDomainInfo.value && ossDomainInfo.value.config_complete && ossDomainInfo.value.oss_domain) {
        // 使用分号分隔多个域名
        return `${mainDomain};${ossDomainInfo.value.oss_domain}`
      }

      return mainDomain
    }

    // 业务域名（必须是 HTTPS）
    const businessDomain = computed(() => `https://${getCurrentDomain()}`)

    // 格式化文件大小
    const formatFileSize = (bytes) => {
      if (!bytes) return '-'
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    }

    // 复制域名
    const copyDomain = async (domain) => {
      try {
        await navigator.clipboard.writeText(domain)
        ElMessage.success({
          message: `已复制: ${domain}`,
          duration: 2000
        })
      } catch (err) {
        ElMessage.error('复制失败，请手动复制')
      }
    }

    // 获取OSS域名配置
    const fetchOssDomain = async () => {
      ossDomainLoading.value = true
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/get_oss_domain.php`, {
          params: {
            store_id: storeId.value,
            agent_id: agentId.value
          }
        })

        if (res.data.code === 200 && res.data.data) {
          ossDomainInfo.value = res.data.data
          console.log('OSS域名获取成功:', ossDomainInfo.value)
        } else {
          console.warn('OSS域名获取失败:', res.data.message)
          ossDomainInfo.value = null
        }
      } catch (err) {
        console.error('获取OSS域名失败:', err)
        ossDomainInfo.value = null
      } finally {
        ossDomainLoading.value = false
      }
    }

    // 获取验证文件信息
    const fetchVerifyFileInfo = async () => {
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/config_api.php`, {
          params: {
            agent_id: agentId.value,
            store_id: storeId.value,
            config_key: 'domain_verify_file'
          }
        })
        if (res.data.code === 200 && res.data.data) {
          verifyFileInfo.value = res.data.data
        } else {
          verifyFileInfo.value = null
        }
      } catch (err) {
        console.error('获取验证文件信息失败:', err)
      }
    }

    // 删除验证文件
    const deleteVerifyFile = async () => {
      try {
        await ElMessageBox.confirm('删除后将无法验证业务域名，确定要删除吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
          center: true
        })

        deletingFile.value = true
        const response = await axios.post(`${config.BASE_URL}/shop/api/delete_domain_file.php`, {
          agent_id: agentId.value,
          store_id: storeId.value,
          config_key: 'domain_verify_file'
        })

        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          verifyFileInfo.value = null
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (err) {
        if (err !== 'cancel') {
          ElMessage.error('删除失败')
        }
      } finally {
        deletingFile.value = false
      }
    }

    // 上传前验证
    const beforeUpload = (file) => {
      const isTxt = file.type === 'text/plain' || file.name.endsWith('.txt')
      const isLt100K = file.size / 1024 < 100

      if (!isTxt) {
        ElMessage.error('只能上传 .txt 格式的文件')
        return false
      }
      if (!isLt100K) {
        ElMessage.error('文件大小不能超过 100KB')
        return false
      }
      return true
    }

    // 上传成功回调
    const handleUploadSuccess = (response) => {
      if (response.code === 200) {
        ElMessage.success('验证文件上传成功')
        verifyFileInfo.value = response.data
        fileList.value = []
        fetchVerifyFileInfo()
      } else {
        ElMessage.error(response.message || '上传失败')
      }
    }

    // 上传失败回调
    const handleUploadError = (error) => {
      console.error('上传错误:', error)
      ElMessage.error('上传失败，请检查网络或稍后重试')
    }

    const fetchConfig = async () => {
      const loading = ElLoading.service({
        text: '加载配置中...',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/config_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, config_key: 'miniprogram' }
        })
        if (res.data.code === 200 && res.data.data) {
          form.appid = res.data.data.appid || ''
          form.upload_key = res.data.data.upload_key || ''
          form.appsecret = res.data.data.appsecret || ''
        }

        await Promise.all([
          fetchVerifyFileInfo(),
          fetchOssDomain()
        ])
      } catch (err) {
        console.error(err)
        ElMessage.error('获取配置失败')
      } finally {
        loading.close()
      }
    }

    const saveConfig = async () => {
      if (!form.appid) {
        ElMessage.warning('请填写AppID')
        return
      }

      submitting.value = true
      const loading = ElLoading.service({
        text: '保存配置中...',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      try {
        const saveData = { appid: form.appid, upload_key: form.upload_key }
        if (form.appsecret) saveData.appsecret = form.appsecret

        const response = await axios.post(`${config.BASE_URL}/shop/api/config_api.php`, {
          action: 'save',
          agent_id: agentId.value,
          store_id: storeId.value,
          config_key: 'miniprogram',
          config_value: JSON.stringify(saveData),
          config_group: 'miniprogram',
          description: '小程序配置'
        })

        if (response.data.code === 200) {
          ElMessage.success('保存成功')
          fetchConfig()
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (err) {
        console.error('保存失败:', err)
        ElMessage.error('保存失败')
      } finally {
        submitting.value = false
        loading.close()
      }
    }

    const openUploadCodeDialog = () => {
      uploadForm.appid = form.appid
      uploadForm.privateKeyContent = form.upload_key
      uploadForm.default_store_id = storeId.value
      uploadForm.api_url = config.BASE_URL
      uploadLogs.value = []
      uploadProgress.value = 0
      uploadProgressStatus.value = ''

      if (!uploadForm.appid) {
        ElMessage.warning('请先配置小程序 AppID')
        return
      }
      if (!uploadForm.privateKeyContent) {
        ElMessage.warning('请先配置小程序上传密钥')
        return
      }

      uploadCodeDialogVisible.value = true
    }

    const submitUploadCode = async () => {
      if (!uploadForm.appid) { ElMessage.warning('请先配置小程序AppID'); return }
      if (!uploadForm.privateKeyContent) { ElMessage.warning('请先配置小程序上传密钥'); return }
      if (!uploadForm.api_url) { ElMessage.warning('API域名未配置'); return }
      if (!uploadForm.default_store_id) { ElMessage.warning('商家ID未配置'); return }

      uploading.value = true
      uploadProgress.value = 10
      uploadProgressStatus.value = ''
      uploadLogs.value = ['开始上传小程序代码...']

      try {
        const response = await axios.post(
          'https://db.juzhendi.com/auth/minidb/group_buy_mini_pro/upload.php',
          {
            appid: uploadForm.appid,
            privateKeyContent: uploadForm.privateKeyContent,
            api_url: uploadForm.api_url,
            default_store_id: String(uploadForm.default_store_id)
          },
          {
            timeout: 120000,
            onUploadProgress: (progressEvent) => {
              const percent = Math.min(90, 10 + Math.floor((progressEvent.loaded || 0) / (progressEvent.total || 1) * 80))
              uploadProgress.value = percent
            }
          }
        )

        uploadProgress.value = 100

        if (response.data.status === 'success') {
          uploadProgressStatus.value = 'success'
          uploadLogs.value.push('✅ 上传成功！')
          if (response.data.detail && Array.isArray(response.data.detail)) {
            response.data.detail.forEach(line => { if (line && line.trim()) uploadLogs.value.push(line) })
          }
          ElMessage.success('小程序代码上传成功')
        } else {
          uploadProgressStatus.value = 'exception'
          uploadLogs.value.push(`❌ 上传失败: ${response.data.message}`)
          ElMessage.error(response.data.message || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        uploadProgressStatus.value = 'exception'
        uploadLogs.value.push(`❌ 请求失败: ${error.message}`)
        if (error.response && error.response.data) {
          uploadLogs.value.push(`错误详情: ${JSON.stringify(error.response.data)}`)
        }
        ElMessage.error('上传失败，请检查网络或稍后重试')
      } finally {
        uploading.value = false
      }
    }

    onMounted(() => {
      fetchConfig()
    })

    return {
      form,
      formRef,
      submitting,
      uploading,
      uploadProgress,
      uploadProgressStatus,
      uploadLogs,
      uploadCodeDialogVisible,
      uploadForm,
      verifyFileInfo,
      deletingFile,
      fileList,
      uploadActionUrl,
      uploadFormData,
      serverDomains,
      businessDomain,
      ossDomainInfo,
      ossDomainLoading,
      ossDomainStatus,
      formatFileSize,
      copyDomain,
      deleteVerifyFile,
      beforeUpload,
      handleUploadSuccess,
      handleUploadError,
      saveConfig,
      openUploadCodeDialog,
      submitUploadCode
    }
  }
}
</script>

<style scoped lang="scss">
.channel-config {
  padding: 24px;
  background: #f5f7fa;
  min-height: 100vh;

  .page-header {
    margin-bottom: 28px;

    .header-content {
      .title-section {
        display: flex;
        align-items: center;
        gap: 16px;

        .title-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;

          .el-icon {
            font-size: 28px;
            color: #fff;
          }
        }

        .page-title {
          font-size: 28px;
          font-weight: 600;
          color: #1a1a2e;
          margin: 0 0 4px 0;
        }

        .page-desc {
          font-size: 14px;
          color: #8c8c8c;
          margin: 0;
        }
      }
    }
  }

  .config-container {
    max-width: 1000px;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .config-card {
    border-radius: 20px;
    border: none;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 2px solid #f0f0f0;
      padding-bottom: 16px;

      .card-header-title {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 18px;
        font-weight: 600;
        color: #1a1a2e;

        .icon-wrapper {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;

          .el-icon {
            font-size: 20px;
          }

          &.primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: #fff;
          }

          &.info {
            background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
            color: #fff;
          }

          &.success {
            background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
            color: #fff;
          }
        }
      }
    }
  }

  .domain-list {
    .domain-item {
      margin-bottom: 20px;
      padding: 16px;
      background: #f8f9fa;
      border-radius: 12px;
      transition: all 0.2s ease;

      &:hover {
        background: #f0f2f5;
      }

      .domain-info {
        .domain-label {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
          flex-wrap: wrap;

          .label-text {
            font-size: 14px;
            font-weight: 500;
            color: #333;
          }
        }

        .domain-value-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;

          .domain-value {
            flex: 1;
            font-family: 'Monaco', 'Menlo', monospace;
            font-size: 14px;
            color: #409eff;
            background: #fff;
            padding: 10px 16px;
            border-radius: 8px;
            border: 1px solid #e4e7ed;
            word-break: break-all;
          }

          .copy-btn {
            flex-shrink: 0;
          }
        }

        .domain-tip {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #909399;
          margin-top: 8px;
          padding-left: 4px;

          .el-icon {
            font-size: 12px;
          }
        }
      }
    }
  }

  .domain-alert {
    margin-top: 20px;
    border-radius: 12px;

    .alert-content {
      font-size: 13px;
      line-height: 1.8;

      p {
        margin: 4px 0;
      }

      .alert-tip {
        color: #e6a23c;
        margin-top: 8px;
      }
    }
  }

  .business-card {
    .business-content {
      .business-domain-section {
        margin-bottom: 24px;

        .section-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #333;
          margin-bottom: 16px;
          font-size: 15px;
        }

        .business-domain-wrapper {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;

          .domain-value {
            flex: 1;
            font-family: 'Monaco', 'Menlo', monospace;
            font-size: 16px;
            font-weight: 600;
            color: #fff;
            word-break: break-all;
          }

          .el-button {
            background: rgba(255, 255, 255, 0.2);
            border-color: rgba(255, 255, 255, 0.3);
            color: #fff;

            &:hover {
              background: rgba(255, 255, 255, 0.3);
            }
          }
        }

        .business-tip {
          margin-top: 12px;
          color: #909399;
        }
      }

      .upload-section {
        .section-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #333;
          margin-bottom: 16px;
          font-size: 15px;
        }

        .upload-description {
          margin-bottom: 20px;
        }

        .file-info-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: #f0f9ff;
          border: 1px solid #b3e0ff;
          border-radius: 12px;
          margin-bottom: 20px;

          .file-icon {
            width: 48px;
            height: 48px;
            background: #409eff;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;

            .el-icon {
              font-size: 28px;
              color: #fff;
            }
          }

          .file-details {
            flex: 1;

            .file-name {
              font-weight: 600;
              color: #333;
              margin-bottom: 6px;
              font-size: 14px;
            }

            .file-meta {
              display: flex;
              gap: 16px;
              font-size: 12px;
              color: #8c8c8c;
            }
          }
        }

        .domain-upload {
          :deep(.el-upload-dragger) {
            width: 100%;
            padding: 40px 20px;
            border: 2px dashed #dcdfe6;
            border-radius: 12px;
            background: #fafafa;
            transition: all 0.3s ease;

            &:hover {
              border-color: #409eff;
              background: #f0f9ff;
            }

            .upload-icon {
              font-size: 48px;
              color: #409eff;
              margin-bottom: 12px;
            }

            .upload-text {
              display: flex;
              flex-direction: column;
              gap: 8px;

              .primary-text {
                font-size: 14px;
                color: #606266;
              }

              .secondary-text {
                font-size: 12px;
                color: #909399;
              }
            }
          }
        }
      }
    }
  }

  .domain-tip {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #909399;
    margin-top: 8px;
    padding-left: 4px;

    .el-icon {
      font-size: 12px;
    }
  }

  .config-form {
    .form-tip {
      font-size: 12px;
      color: #999;
      margin-top: 4px;
      line-height: 1.4;
    }

    .save-btn-item {
      margin-top: 24px;

      .btn-group {
        display: flex;
        gap: 16px;

        .el-button {
          padding: 10px 28px;
          font-weight: 500;
        }
      }
    }
  }

  .upload-progress {
    margin-top: 24px;

    .upload-log {
      margin-top: 12px;
      max-height: 300px;
      overflow-y: auto;
      background: #1e1e1e;
      border-radius: 8px;
      padding: 12px;

      .log-line {
        font-family: 'Monaco', 'Menlo', monospace;
        font-size: 12px;
        color: #d4d4d4;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-all;
      }
    }
  }
}

:deep(.el-dialog) {
  border-radius: 16px;

  .el-dialog__header {
    border-bottom: 1px solid #f0f0f0;
    padding: 20px 24px;
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid #f0f0f0;
    padding: 16px 24px;
  }
}

:deep(.el-alert) {
  border-radius: 10px;
}
</style>